import type { WootricConfig } from '../types/wootric';
import type { FeedbackCollectorConfig } from '../types/feedbackCollector';
import type { IntercomConfig } from '../types/intercom';

let cachedWootricConfig: WootricConfig | null = null;
let cachedFeedbackCollectorConfig: FeedbackCollectorConfig | null = null;
let cachedIntercomConfig: IntercomConfig | null = null;

const defaultWootricConfig: WootricConfig = {
  wootricDomain: '',
  productionAccountId: '',
  testAccountId: '',
  product: '',
  productReportingCode: '',
  linkUrl: '',
  linkWord: '',
  blacklistedDomains: [],
};

export const configService = {
  async getWootricConfig(): Promise<WootricConfig> {
    if (cachedWootricConfig) {
      return cachedWootricConfig;
    }

    try {
      const response = await fetch('/config/wootric.config.json');
      if (!response.ok) {
          console.warn('Wootric config not found, using defaults');
          return defaultWootricConfig;
      }

      cachedWootricConfig = await response.json();
      return cachedWootricConfig!;
    } catch (error) {
      console.error('Failed to load Wootric config:', error);
      return defaultWootricConfig;
    }
  },

  async getFeedbackCollectorConfig(): Promise<FeedbackCollectorConfig> {
    if (cachedFeedbackCollectorConfig) {
      return cachedFeedbackCollectorConfig;
    }

    const accountToken = import.meta.env.VITE_FEEDBACK_COLLECTOR_ACCOUNT_TOKEN ?? '31den6jwxbxqkghg4bfj28iga63a0a52';

    const defaultFeedbackCollectorConfig: FeedbackCollectorConfig = {
      apiHost: 'https://api.sentiment.accessacloud.com',
      accountToken,
      product: 'Access Time',
    };

    try {
      const response = await fetch('/config/feedbackCollector.config.json');
      if (!response.ok) {
        console.warn('Feedback collector config not found, using defaults');
        return defaultFeedbackCollectorConfig;
      }

      const configFromFile = await response.json();
      // Merge config from file with accountToken from environment variable
      cachedFeedbackCollectorConfig = {
        ...configFromFile,
        accountToken,
      };
      return cachedFeedbackCollectorConfig!;
    } catch (error) {
      console.error('Failed to load feedback collector config:', error);
      return defaultFeedbackCollectorConfig;
    }
  },

  async getIntercomConfig(): Promise<IntercomConfig> {
    if (cachedIntercomConfig) {
      return cachedIntercomConfig;
    }

    const defaultIntercomConfig: IntercomConfig = { appId: '' };

    try {
      const response = await fetch('/config/intercom.config.json');
      if (!response.ok) {
        console.warn('Intercom config not found, using defaults');
        return defaultIntercomConfig;
      }

      cachedIntercomConfig = await response.json();
      return cachedIntercomConfig!;
    } catch (error) {
      console.error('Failed to load Intercom config:', error);
      return defaultIntercomConfig;
    }
  },

  clearCache(): void {
    cachedWootricConfig = null;
    cachedFeedbackCollectorConfig = null;
    cachedIntercomConfig = null;
  },
};
